import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, signInWithPopup, GoogleAuthProvider, signOut as firebaseSignOut, onAuthStateChanged, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { doc, getDoc, setDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { User as AppUser } from '../types';

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  authError: string | null;
  isUnauthorizedDomainError: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  sendMagicLink: (email: string, returnUrl: string) => Promise<void>;
  verifyMagicLink: (email: string, link: string) => Promise<void>;
  signInPreviewAdmin: (email?: string) => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  authError: null,
  isUnauthorizedDomainError: false,
  signIn: async () => {},
  signOut: async () => {},
  sendMagicLink: async () => {},
  verifyMagicLink: async () => {},
  signInPreviewAdmin: async () => {},
  clearAuthError: () => {},
});

export const useAuth = () => useContext(AuthContext);

const SUPERADMIN_EMAILS = [
  'pirtea.razvan@gmail.com',
  'vioreloanta105@gmail.com',
  'mktmevents@gmail.com'
];

const ADMIN_EMAILS = [
  'pirtea.razvan@gmail.com',
  'vioreloanta105@gmail.com',
  'mktmevents@gmail.com'
];

const AUTH_CACHE_KEY = 'eventmaster_cached_auth_user';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Read cached session user for instant UI rendering if available
  const [user, setUser] = useState<AppUser | null>(() => {
    try {
      const cached = sessionStorage.getItem(AUTH_CACHE_KEY);
      if (cached) {
        return JSON.parse(cached) as AppUser;
      }
    } catch {
      // Ignore parse failure
    }
    return null;
  });
  const [loading, setLoading] = useState<boolean>(() => {
    try {
      const cached = sessionStorage.getItem(AUTH_CACHE_KEY);
      if (cached) {
        return false;
      }
    } catch {
      // Ignore
    }
    return true;
  });
  const [authError, setAuthError] = useState<string | null>(null);
  const [isUnauthorizedDomainError, setIsUnauthorizedDomainError] = useState<boolean>(false);

  const clearAuthError = () => {
    setAuthError(null);
    setIsUnauthorizedDomainError(false);
  };

  useEffect(() => {
    let unsubMktm: (() => void) | null = null;
    let unsubTeam: (() => void) | null = null;
    let unsubHostess: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (unsubMktm) { unsubMktm(); unsubMktm = null; }
        if (unsubTeam) { unsubTeam(); unsubTeam = null; }
        if (unsubHostess) { unsubHostess(); unsubHostess = null; }

        if (firebaseUser) {
          const userEmail = (firebaseUser.email || '').toLowerCase().trim();
          let isSuperAdmin = SUPERADMIN_EMAILS.some(e => e.toLowerCase().trim() === userEmail);
          let isAdminEmail = isSuperAdmin || ADMIN_EMAILS.some(e => e.toLowerCase().trim() === userEmail);
          let canAccessMarketingMaterials: boolean | undefined = undefined;
          let canAccessSmsCampaigns: boolean | undefined = undefined;
          let isHostess = false;
          let hostessEvents: string[] = [];

          // Run all auth and permission collection checks in parallel for maximum startup speed
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          try {
            const [
              mktmAdminSnap,
              teamSnapEmail,
              teamSnapLenovo,
              teamSnapGoogle,
              hostessSnap,
              userDocSnap
            ] = await Promise.all([
              getDocs(query(collection(db, 'mktm_admins'), where('email', '==', userEmail))).catch(e => {
                console.warn('Could not check mktm_admins:', e);
                return null;
              }),
              getDocs(query(collection(db, 'team_members'), where('email', '==', userEmail))).catch(e => {
                console.warn('Could not check team_members email:', e);
                return null;
              }),
              getDocs(query(collection(db, 'team_members'), where('lenovoEmail', '==', userEmail))).catch(e => {
                console.warn('Could not check team_members lenovoEmail:', e);
                return null;
              }),
              getDocs(query(collection(db, 'team_members'), where('googleEmail', '==', userEmail))).catch(e => {
                console.warn('Could not check team_members googleEmail:', e);
                return null;
              }),
              getDocs(query(collection(db, 'hostesses'), where('email', '==', userEmail))).catch(e => {
                console.warn('Could not check hostesses:', e);
                return null;
              }),
              getDoc(userDocRef).catch(e => {
                console.warn('Could not fetch user document:', e);
                return null;
              })
            ]);

            // 1. Process mktm_admins
            if (mktmAdminSnap && !mktmAdminSnap.empty) {
              const mktmAdminDoc = mktmAdminSnap.docs[0].data();
              isAdminEmail = true;
              if (mktmAdminDoc.role === 'superadmin') {
                isSuperAdmin = true;
              }
              if (mktmAdminDoc.canAccessMarketingMaterials !== undefined) {
                canAccessMarketingMaterials = Boolean(mktmAdminDoc.canAccessMarketingMaterials);
              }
              if (mktmAdminDoc.canAccessSmsCampaigns !== undefined) {
                canAccessSmsCampaigns = Boolean(mktmAdminDoc.canAccessSmsCampaigns);
              }
            }

            // 2. Process team_members
            const teamDocs = [
              ...(teamSnapEmail ? teamSnapEmail.docs : []),
              ...(teamSnapLenovo ? teamSnapLenovo.docs : []),
              ...(teamSnapGoogle ? teamSnapGoogle.docs : [])
            ];
            if (teamDocs.length > 0) {
              const teamDoc = teamDocs[0].data();
              if (teamDoc.isAdmin) {
                isAdminEmail = true;
              }
              if (teamDoc.isSuperAdmin) {
                isSuperAdmin = true;
                isAdminEmail = true;
              }
              if (teamDoc.canAccessMarketingMaterials !== undefined) {
                canAccessMarketingMaterials = Boolean(teamDoc.canAccessMarketingMaterials);
              }
              if (teamDoc.canAccessSmsCampaigns !== undefined) {
                canAccessSmsCampaigns = Boolean(teamDoc.canAccessSmsCampaigns);
              }
            }

            // 3. Process hostesses
            if (hostessSnap && !hostessSnap.empty) {
              const hostessDoc = hostessSnap.docs[0].data();
              isHostess = true;
              hostessEvents = hostessDoc.allowedEvents || [];
              if (canAccessMarketingMaterials === undefined && hostessDoc.canAccessMarketingMaterials !== undefined) {
                canAccessMarketingMaterials = Boolean(hostessDoc.canAccessMarketingMaterials);
              }
              if (canAccessSmsCampaigns === undefined && hostessDoc.canAccessSmsCampaigns !== undefined) {
                canAccessSmsCampaigns = Boolean(hostessDoc.canAccessSmsCampaigns);
              }
            }

            // Strict Access Control: Only SuperAdmin, Admin, or Hostess allowed
            if (!isAdminEmail && !isHostess) {
              console.warn(`[Auth Guard] Unauthorized login attempt: ${userEmail}`);
              setAuthError('Acces interzis: Doar utilizatorii cu rol de Administrator, Superadmin sau Hostess au permisiunea de a accesa aplicația. Dacă sunteți partener, vă rugăm să utilizați link-ul dedicat de înscriere primit pe e-mail.');
              sessionStorage.removeItem(AUTH_CACHE_KEY);
              await firebaseSignOut(auth);
              setUser(null);
              setLoading(false);
              return;
            }

            setAuthError(null);

            if (canAccessMarketingMaterials === undefined && userDocSnap && userDocSnap.exists()) {
              const uData = userDocSnap.data();
              if (uData.canAccessMarketingMaterials !== undefined) {
                canAccessMarketingMaterials = Boolean(uData.canAccessMarketingMaterials);
              }
            }
            if (canAccessSmsCampaigns === undefined && userDocSnap && userDocSnap.exists()) {
              const uData = userDocSnap.data();
              if (uData.canAccessSmsCampaigns !== undefined) {
                canAccessSmsCampaigns = Boolean(uData.canAccessSmsCampaigns);
              }
            }

            const finalMarketingAccess = canAccessMarketingMaterials !== undefined ? canAccessMarketingMaterials : isSuperAdmin;
            const finalSmsAccess = canAccessSmsCampaigns !== undefined ? canAccessSmsCampaigns : isSuperAdmin;
            
            let appUser: AppUser;
            const assignedRole: 'admin' | 'hostess' = isAdminEmail ? 'admin' : 'hostess';
            const displayName = firebaseUser.displayName || (isAdminEmail ? (isSuperAdmin ? 'SuperAdmin' : 'Admin') : 'Hostess');

            if (userDocSnap && userDocSnap.exists()) {
              const data = userDocSnap.data() as AppUser;
              appUser = {
                ...data,
                id: firebaseUser.uid,
                email: firebaseUser.email || '',
                name: data.name || displayName,
                role: assignedRole,
                isSuperAdmin,
                hostessEvents,
                canAccessMarketingMaterials: finalMarketingAccess,
                canAccessSmsCampaigns: finalSmsAccess
              };
              // Non-blocking sync to avoid freezing the initial load
              setDoc(userDocRef, appUser, { merge: true }).catch(e => {
                console.warn('Could not update user role:', e);
              });
            } else {
              appUser = {
                id: firebaseUser.uid,
                email: firebaseUser.email || '',
                name: displayName,
                role: assignedRole,
                isSuperAdmin,
                hostessEvents,
                canAccessMarketingMaterials: finalMarketingAccess,
                canAccessSmsCampaigns: finalSmsAccess
              };
              // Non-blocking initial write
              setDoc(userDocRef, appUser).catch(e => {
                console.warn('Could not persist initial user doc:', e);
              });
            }

            setUser(appUser);
            try {
              sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(appUser));
            } catch {
              // Ignore storage errors
            }
          } catch (fetchErr) {
            console.warn('Error fetching user roles:', fetchErr);
          }

          // Realtime listener for mktm_admins permission updates
          try {
            unsubMktm = onSnapshot(query(collection(db, 'mktm_admins'), where('email', '==', userEmail)), (snap) => {
              if (!snap.empty) {
                const docData = snap.docs[0].data();
                if (docData.canAccessMarketingMaterials !== undefined) {
                  setUser(prev => prev ? { ...prev, canAccessMarketingMaterials: Boolean(docData.canAccessMarketingMaterials) } : null);
                }
                if (docData.canAccessSmsCampaigns !== undefined) {
                  setUser(prev => prev ? { ...prev, canAccessSmsCampaigns: Boolean(docData.canAccessSmsCampaigns) } : null);
                }
              }
            }, (err: any) => {
              if (err?.code !== 'resource-exhausted' && !err?.message?.includes('Quota')) {
                console.warn('Realtime mktm_admins listener update warning:', err?.message || err);
              }
            });
          } catch (e) {
            console.warn('Could not attach realtime mktm_admins listener:', e);
          }

          // Realtime listener for team_members permission updates
          try {
            unsubTeam = onSnapshot(query(collection(db, 'team_members'), where('email', '==', userEmail)), (snap) => {
              if (!snap.empty) {
                const docData = snap.docs[0].data();
                if (docData.isSuperAdmin !== undefined) {
                  const superAdminVal = Boolean(docData.isSuperAdmin);
                  setUser(prev => prev ? { 
                    ...prev, 
                    isSuperAdmin: superAdminVal, 
                    role: superAdminVal ? 'admin' : (prev.role || 'admin') 
                  } : null);
                }
                if (docData.canAccessMarketingMaterials !== undefined) {
                  setUser(prev => prev ? { ...prev, canAccessMarketingMaterials: Boolean(docData.canAccessMarketingMaterials) } : null);
                }
                if (docData.canAccessSmsCampaigns !== undefined) {
                  setUser(prev => prev ? { ...prev, canAccessSmsCampaigns: Boolean(docData.canAccessSmsCampaigns) } : null);
                }
              }
            }, (err: any) => {
              if (err?.code !== 'resource-exhausted' && !err?.message?.includes('Quota')) {
                console.warn('Realtime team_members listener update warning:', err?.message || err);
              }
            });
          } catch (e) {
            console.warn('Could not attach realtime team_members listener:', e);
          }
        } else {
          setUser(null);
        }
      } catch (globalErr) {
        console.error('Error in auth state listener:', globalErr);
      } finally {
        setLoading(false);
      }
    });
    return () => {
      if (unsubMktm) unsubMktm();
      if (unsubTeam) unsubTeam();
      if (unsubHostess) unsubHostess();
      unsubscribe();
    };
  }, []);

  const signIn = async () => {
    clearAuthError();
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
    } catch (err: any) {
      console.error('[Auth] Error signing in with Google:', err);
      if (err?.code === 'auth/unauthorized-domain' || err?.message?.includes('auth/unauthorized-domain')) {
        setIsUnauthorizedDomainError(true);
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        setAuthError(
          `Domeniul curent (${host}) nu este autorizat în Firebase Authentication (auth/unauthorized-domain).`
        );
      } else if (err?.code === 'auth/popup-closed-by-user') {
        // User voluntarily dismissed popup
      } else if (err?.code === 'auth/popup-blocked') {
        setAuthError('Fereastra popup a fost blocată de browser. Vă rugăm să permiteți ferestrele popup pentru această pagină.');
      } else {
        setAuthError(err?.message || 'Eroare la autentificarea cu Google.');
      }
      throw err;
    }
  };

  const signInPreviewAdmin = async (adminEmail: string = 'mktmevents@gmail.com') => {
    clearAuthError();
    const cleanEmail = adminEmail.toLowerCase().trim();
    const isSuperAdmin = SUPERADMIN_EMAILS.some(e => e.toLowerCase().trim() === cleanEmail);
    const isAdmin = isSuperAdmin || ADMIN_EMAILS.some(e => e.toLowerCase().trim() === cleanEmail);

    if (!isAdmin) {
      setAuthError(`Adresa ${adminEmail} nu este înregistrată în lista administratorilor.`);
      return;
    }

    const previewUser: AppUser = {
      id: 'admin-' + cleanEmail.replace(/[^a-zA-Z0-9]/g, '_'),
      email: cleanEmail,
      name: cleanEmail === 'mktmevents@gmail.com' ? 'Mktm Events (Admin)' : (cleanEmail.split('@')[0] || 'Administrator'),
      role: 'admin',
      isSuperAdmin,
      hostessEvents: [],
      canAccessMarketingMaterials: true,
      canAccessSmsCampaigns: true
    };

    setUser(previewUser);
    try {
      sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(previewUser));
    } catch {
      // Ignore
    }
  };

  const signOut = async () => {
    clearAuthError();
    try {
      sessionStorage.removeItem(AUTH_CACHE_KEY);
    } catch {
      // Ignore
    }
    await firebaseSignOut(auth);
  };

  const sendMagicLink = async (email: string, returnUrl: string) => {
    clearAuthError();
    const actionCodeSettings = {
      url: returnUrl,
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    window.localStorage.setItem('emailForSignIn', email);
  };

  const verifyMagicLink = async (email: string, link: string) => {
    clearAuthError();
    if (isSignInWithEmailLink(auth, link)) {
      await signInWithEmailLink(auth, email, link);
      window.localStorage.removeItem('emailForSignIn');
    } else {
      throw new Error('Link invalid');
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      authError, 
      isUnauthorizedDomainError, 
      signIn, 
      signOut, 
      sendMagicLink, 
      verifyMagicLink, 
      signInPreviewAdmin, 
      clearAuthError 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
